import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, ArrowUpRight, Sparkles, Users } from "lucide-react";

const STAGE_COLORS = {
  new: "bg-slate-100 text-slate-700",
  screened: "bg-blue-50 text-blue-700",
  interview: "bg-amber-50 text-amber-700",
  offer: "bg-purple-50 text-purple-700",
  hired: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
};

export default function TalentPool() {
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("all"); // all | unassigned | assigned
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", cv_text: "", file: null, job_id: "__none__" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [c, j] = await Promise.all([api.get("/candidates"), api.get("/jobs")]);
    setCandidates(c.data);
    setJobs(j.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.full_name) { toast.error("Nom requis"); return; }
    if (!form.cv_text && !form.file) { toast.error("CV requis (fichier ou texte)"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("full_name", form.full_name);
      if (form.job_id && form.job_id !== "__none__") fd.append("job_id", form.job_id);
      if (form.email) fd.append("email", form.email);
      if (form.phone) fd.append("phone", form.phone);
      if (form.cv_text) fd.append("cv_text", form.cv_text);
      if (form.file) fd.append("file", form.file);
      await api.post("/candidates/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(form.job_id === "__none__" ? "Candidat ajouté au pool · L'IA va suggérer les meilleurs postes" : "Candidat ajouté");
      setOpen(false);
      setForm({ full_name: "", email: "", phone: "", cv_text: "", file: null, job_id: "__none__" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const filtered = candidates.filter((c) => {
    if (filter === "unassigned") return !c.job_id;
    if (filter === "assigned") return !!c.job_id;
    return true;
  });

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-slate-500 mb-2">Pool talents</div>
          <h1 className="font-display text-4xl tracking-tight">Tous vos candidats</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">Uploadez un CV et laissez l'IA suggérer le meilleur poste pour ce candidat avec preuves de compétences.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill px-6" data-testid="pool-add-btn">
              <Upload className="w-4 h-4 mr-2" /> Ajouter un candidat
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Nouveau candidat</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Nom complet *</Label>
                <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1.5" data-testid="pool-form-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="pool-form-email" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Téléphone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="pool-form-phone" /></div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Poste (optionnel)</Label>
                <Select value={form.job_id} onValueChange={(v) => setForm({ ...form, job_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="pool-form-job">
                    <SelectValue placeholder="Laisser l'IA choisir…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      <span className="flex items-center gap-2"><Sparkles className="w-3 h-3" /> Laisser l'IA choisir le meilleur poste</span>
                    </SelectItem>
                    {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="text-xs text-slate-500 mt-1.5">
                  {form.job_id === "__none__"
                    ? "→ L'IA analysera le CV et suggérera automatiquement les postes qui matchent le mieux."
                    : "→ Analyse ciblée sur ce poste."}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Fichier CV (PDF ou DOCX)</Label>
                <input type="file" accept=".pdf,.docx" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="mt-1.5 block w-full text-sm" data-testid="pool-form-file" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Ou coller texte du CV</Label>
                <Textarea value={form.cv_text} onChange={(e) => setForm({ ...form, cv_text: e.target.value })} className="mt-1.5 min-h-[120px] font-mono text-xs" data-testid="pool-form-text" />
              </div>
              <Button onClick={submit} disabled={loading} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill h-11" data-testid="pool-form-submit">
                {loading ? "Envoi…" : "Ajouter le candidat"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <Button variant={filter === "all" ? "default" : "outline"} size="sm" onClick={() => setFilter("all")} className="btn-pill" data-testid="filter-all">Tous ({candidates.length})</Button>
        <Button variant={filter === "unassigned" ? "default" : "outline"} size="sm" onClick={() => setFilter("unassigned")} className="btn-pill" data-testid="filter-unassigned">Sans poste ({candidates.filter((c) => !c.job_id).length})</Button>
        <Button variant={filter === "assigned" ? "default" : "outline"} size="sm" onClick={() => setFilter("assigned")} className="btn-pill" data-testid="filter-assigned">Assignés ({candidates.filter((c) => c.job_id).length})</Button>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-16 text-center bg-white">
          <Users className="w-8 h-8 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">Aucun candidat dans cette vue.</p>
          <Button onClick={() => setOpen(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill" data-testid="empty-pool-add">+ Ajouter un candidat</Button>
        </div>
      ) : (
        <div className="border border-slate-200 bg-white">
          {filtered.map((c) => {
            const score = c.ai?.justification?.match_score;
            const bestMatch = c.ai?.job_matches?.matches?.[0];
            return (
              <Link key={c.id} to={`/app/candidates/${c.id}`} className="flex items-center gap-4 p-5 border-b last:border-b-0 border-slate-100 hover:bg-slate-50 group" data-testid={`pool-row-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{c.full_name}</div>
                  <div className="text-xs text-slate-500 mt-1 font-mono truncate">{c.email || "—"}</div>
                </div>
                <div className="hidden md:block text-sm">
                  {c.job_title ? (
                    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 font-mono text-xs">{c.job_title}</Badge>
                  ) : bestMatch ? (
                    <Badge className="bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-50 font-mono text-xs">
                      <Sparkles className="w-3 h-3 mr-1" /> IA : {bestMatch.job_title}
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-50 text-amber-700 border border-amber-100 hover:bg-amber-50 font-mono text-xs">Sans poste</Badge>
                  )}
                </div>
                <div className="w-24 text-right">
                  {score != null ? (
                    <div className="font-mono text-2xl font-bold tabular-nums">{score}<span className="text-xs text-slate-400">/100</span></div>
                  ) : bestMatch ? (
                    <div className="font-mono text-2xl font-bold tabular-nums text-blue-600">{bestMatch.score}<span className="text-xs text-slate-400">/100</span></div>
                  ) : (
                    <div className="text-xs text-slate-400 italic">Non analysé</div>
                  )}
                </div>
                <Badge className={`${STAGE_COLORS[c.stage] || "bg-slate-100 text-slate-700"} hover:opacity-100 font-mono text-xs`}>{c.stage}</Badge>
                <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-slate-900" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
